<template>
  <div>
    <b-row>
      <b-col md="6" class="mb-2">
        <FormulateForm>
          <FormulateInput
            id="vGroup"
            ref="vGroup-ref"
            v-model="fleetGroup"
            name="vGroup"
            class="mt-2"
            :label="$t('global.object.fleetGroup')"
            :placeholder="$t('global.form.select') + ' ' + $t('global.object.fleetGroup')"
            trackBy="id"
            displayedBy="name"
            :multiple="true"
            type="select-resourceful-v3"
            :optionsGetter="resourcefulQuery.fleetGroup"
            :optionLabelFn="({ item }) => item.name"
            :fullObject="fullObject.fleetGroup"
            selectedLabel="name"
            :hasClose="false"
            @update:fullObject="($event) => (fullObject.fleetGroup = $event)"
          />
        </FormulateForm>
      </b-col>
      <b-col md="3">
        <McButton
          class="mc-button__position"
          variant="primary"
          :text="$t('global.action.apply')"
          :block="true"
          @click="handleApply()"
        />
      </b-col>
      <b-col md="3">
        <McButton
          class="mc-button__position"
          variant="secondary"
          :text="$t('global.action.resetConfig')"
          :block="true"
          @click="handleResetConfig()"
        />
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col md="6">
        <h6 v-if="multiple && totalSelected" class="mc-row-check">
          <span class="badge btn-success">{{ totalSelected }}</span>
          {{ $t('global.object.selectedRow') }}
          <a class="color-primary" @click="$emit('unselectAll')">{{
            $t('global.action.deselect')
          }}</a>
        </h6>
      </b-col>
      <b-col md="6">
        <b-input-group size="sm" class="mc-has-search mc-input" @click="focusSearch">
          <template #prepend>
            <i class="mc-icon icon-fill_search"></i>
          </template>

          <b-form-input
            ref="searchInput"
            :value="search"
            :disabled="isBusy || isEditing"
            :placeholder="$t('global.search') + '...'"
            @input="handleSearchChange"
          />

          <template v-if="search" #append>
            <button
              class="clear-search-btn"
              :disabled="isBusy || isEditing"
              @click="handleClearSearch"
            >
              <i class="pe-2 mc-icon icon-outline_close"></i>
            </button>
          </template>
        </b-input-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import McButton from '@/components/McButton'
import { GET_VGROUPS_PAGINATED } from '@/services/api/vehicle/vgroup'

export default {
  components: {
    McButton,
  },
  props: {
    attrs: {
      type: Object,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    isBusy: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
    },
    totalSelected: {
      type: Number,
    },
  },
  emits: ['update:search', 'unselectAll', 'update:fleetGroup'],
  data() {
    return {
      fullObject: {
        fleetGroup: [],
      },
      fleetGroup: null,
    }
  },
  computed: {
    resourcefulQuery() {
      return {
        fleetGroup: async (opts) => {
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            query: GET_VGROUPS_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                excludeVgroupId: this.fleetGroupIds,
              },
            },
          })

          const data = {
            metadata: {
              total_count: res.data.vgroupsPaginated.vgroups.pagination.total,
              page: res.data.vgroupsPaginated.vgroups.pagination.page,
            },
            data: {
              paginated_result: res.data.vgroupsPaginated.vgroups.data,
            },
          }

          return data
        },
      }
    },
  },
  methods: {
    handleSearchChange: debounce(function (_search) {
      this.$emit('update:search', _search)
    }, 500),
    handleClearSearch() {
      this.$emit('update:search', '')
    },
    focusSearch() {
      this.$refs['searchInput'].focus()
    },
    handleApply() {
      this.$store.dispatch('route/setFleetGroup', this.fleetGroup)
    },
    handleResetConfig() {
      this.fleetGroup = null
      this.$store.dispatch('route/setFleetGroup', null)
    },
  },
}
</script>

<style lang="scss" scoped>
// TODO: Refactor this selector
.mc-input {
  background: $white;
  border: 1px solid $teal-200;
  border-radius: 4px;
  line-height: 16px;
  font-family: $font-family-sans-serif;
  color: #344264;
  font-size: 12px;
  width: 100%;
  justify-content: space-between;

  &.input-md {
    height: 37px;
    padding: 10.5px 10px;
  }

  &.input-sm {
    height: 27px;
    padding: 5.5px 10px;
  }

  & > div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    cursor: pointer;
  }

  input {
    padding: 0px;
  }

  input::placeholder {
    color: $teal-200;
    font-size: 12px;
  }

  i {
    &::before {
      display: block;
      color: #67718b;
      font-size: 20px;
      line-height: none;
    }
  }

  &.__disabled {
    background-color: $gray;
    color: $teal-200;
    cursor: default;

    i::before {
      color: $teal-200;
    }

    input {
      background-color: $gray;
    }
  }
}

.__table-top {
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.mc-has-search {
  padding: 10.5px;
  padding-left: 10px;
  margin-left: auto;

  // if search is disabled
  &:has(input:disabled) {
    background: $input-disabled-bg;
  }

  .clear-search-btn {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .clear-search-btn:disabled {
    cursor: default;
  }

  input {
    border: none;
    border-radius: 4px;

    padding: 0;
    padding-left: 8px;

    font-size: 12px;
  }

  .input-group-prepend,
  .input-group-append {
    border: none;
    display: flex;
    align-items: center;

    .mc-icon {
      padding-left: 0;
      &:before {
        font-size: 16px;
        color: $input-placeholder-color;
      }
    }
  }
}
.mc-button__position {
  margin-top: 40px;
}
</style>
